import { compileApplicationComposition } from "../composition/compile-application-composition"
import {
  admitProjectValidationAuthorityEnvelope,
  hashProjectValidationAuthorityValue,
} from "../composition/admit-application-composition"
import { compileLibraryComposition } from "../library-composition/compile-library-composition"
import { admitLibraryAuthorityContext } from "../library-composition/admit-library-composition"
import {
  compileSourceBoundInteractionContract,
  computeSourceBoundInteractionSubject,
} from "../source-bound-interaction-contract"
import {
  resolveApplicationProfileLibraryContract,
  type ApplicationProfileLibraryContract,
} from "../application-profile-library-contract"
import type {
  DesignBoundApplicationGap,
  DesignBoundApplicationInput,
  DesignBoundApplicationResult,
  DesignOntologyReleaseResolver,
} from "./types"

export async function compileDesignBoundApplication(
  input: DesignBoundApplicationInput,
  resolver: DesignOntologyReleaseResolver,
): Promise<DesignBoundApplicationResult> {
  const releaseRef = input.designOntologyReleaseRef?.trim()
  if (!releaseRef) return missingReleaseRef()

  // Task 10 — the generic profile-side authority. Resolved through the ONE
  // closed guard, and refused when supplied-but-unadmitted: a contract that is
  // present and silently ignored is indistinguishable, from the caller's side,
  // from one that was honoured.
  let profileLibraryContract: ApplicationProfileLibraryContract | undefined
  if (input.profileLibraryContract !== undefined) {
    const resolution = resolveApplicationProfileLibraryContract(
      input.profileLibraryContract,
    )
    if (!resolution.ok) return projectAuthorityMismatch(resolution.blocker)
    profileLibraryContract = resolution.contract
  }
  void profileLibraryContract

  const projectAuthorityInput = captureProjectAuthorityInput(input)
  if (!projectAuthorityInput.ok) {
    return projectAuthorityMismatch("project-authority-envelope-invalid")
  }
  let projectValidationAuthority
  if (projectAuthorityInput.present) {
    const admittedAuthority = admitProjectValidationAuthorityEnvelope(
      projectAuthorityInput.value,
    )
    if (!admittedAuthority) {
      return projectAuthorityMismatch("project-authority-envelope-invalid")
    }
    projectValidationAuthority = admittedAuthority
  }

  const authorityAdmission = admitLibraryAuthorityContext(
    input.libraryAuthority,
  )
  if (!authorityAdmission.ok) return authorityAdmission
  const admittedLibraryAuthority = authorityAdmission.authorityContext
  const library = compileLibraryComposition(input.library, admittedLibraryAuthority)
  if (!library.ok) return library
  let libraryUsesProjectAuthority: boolean
  try {
    libraryUsesProjectAuthority =
      typeof admittedLibraryAuthority === "object" &&
      admittedLibraryAuthority !== null &&
      Reflect.has(
        admittedLibraryAuthority,
        "approvedProjectValidationContractRefs",
      )
  } catch {
    return projectAuthorityMismatch(
      projectValidationAuthority?.authorityContext.contractHash ??
        "authority-context-unreadable",
    )
  }
  if (
    libraryUsesProjectAuthority !==
    Boolean(projectValidationAuthority)
  ) {
    return projectAuthorityMismatch(
      projectValidationAuthority?.authorityContext.contractHash ??
        "project-authority-required",
    )
  }
  if (projectValidationAuthority) {
    let libraryAuthorityHash: string
    try {
      libraryAuthorityHash = hashProjectValidationAuthorityValue(
        admittedLibraryAuthority,
      )
    } catch {
      return projectAuthorityMismatch(
        projectValidationAuthority.authorityContext.contractHash,
        projectValidationAuthority.authorityContext
          .approvedProjectValidationContractRefs,
      )
    }
    if (
      libraryAuthorityHash !==
        projectValidationAuthority.authorityContextHash
    ) {
      return projectAuthorityMismatch(
        projectValidationAuthority.authorityContext.contractHash,
        projectValidationAuthority.authorityContext
          .approvedProjectValidationContractRefs,
      )
    }
  }

  let release
  try {
    release = await resolver.resolveDesignOntologyRelease(releaseRef)
  } catch {
    return resolutionFailed(releaseRef)
  }
  if (!release) return releaseNotFound(releaseRef)
  if (release.releaseId !== releaseRef) {
    return releaseRefMismatch(releaseRef, release.releaseId)
  }

  const design = compileApplicationComposition(
    library.artifact.plan,
    release,
    projectValidationAuthority,
  )
  if (!design.ok) return design

  const {
    applicationSemanticHash,
    designBindingHash,
    renderContractHash,
  } = await computeSourceBoundInteractionSubject({
    library: library.artifact,
    design: design.artifact,
  })
  const sourceBoundInteractionContract =
    await compileSourceBoundInteractionContract({
      library: library.artifact,
      design: design.artifact,
      designOntologyReleaseRef: releaseRef,
      applicationSemanticHash,
      designBindingHash,
      renderContractHash,
      sourceInteractionRelations: input.sourceInteractionRelations,
    })
  if (sourceBoundInteractionContract.status === "blocked") {
    return { ok: false, gaps: sourceBoundInteractionContract.issues }
  }

  return {
    ok: true,
    artifact: {
      schemaVersion: "cogo.design-bound-application-artifact.v1",
      designOntologyReleaseRef: releaseRef,
      library: library.artifact,
      design: design.artifact,
      applicationSemanticHash,
      designBindingHash,
      renderContractHash,
      sourceBoundInteractionContract: sourceBoundInteractionContract.contract,
      generationTrace: {
        mode: "mandatory-design-ontology-binding",
        designOntologyRequired: true,
        genericFallbackUsed: false,
        rawPresentationAuthoredByModel: false,
        ...(projectValidationAuthority
          ? {
              projectValidationAuthority: structuredClone(
                projectValidationAuthority,
              ),
            }
          : {}),
      },
    },
  }
}

function captureProjectAuthorityInput(
  input: DesignBoundApplicationInput,
):
  | { ok: true; present: false }
  | { ok: true; present: true; value: unknown }
  | { ok: false } {
  try {
    const descriptor = Reflect.getOwnPropertyDescriptor(
      input,
      "projectValidationAuthority",
    )
    if (!descriptor) {
      return Reflect.has(input, "projectValidationAuthority")
        ? { ok: false }
        : { ok: true, present: false }
    }
    if (!("value" in descriptor) || !descriptor.enumerable) {
      return { ok: false }
    }
    return descriptor.value === undefined
      ? { ok: true, present: false }
      : { ok: true, present: true, value: descriptor.value }
  } catch {
    return { ok: false }
  }
}

function missingReleaseRef(): DesignBoundApplicationResult {
  return {
    ok: false,
    gaps: [
      gap(
        "design-ontology-release-ref-missing",
        "$.designOntologyReleaseRef",
        "A Design Ontology release reference is required.",
        [],
      ),
    ],
  }
}

function releaseNotFound(releaseRef: string): DesignBoundApplicationResult {
  return {
    ok: false,
    gaps: [
      gap(
        "design-ontology-release-not-found",
        "$.designOntologyReleaseRef",
        "The requested Design Ontology release was not found.",
        [releaseRef],
      ),
    ],
  }
}

function releaseRefMismatch(
  releaseRef: string,
  _releaseId: string,
): DesignBoundApplicationResult {
  return {
    ok: false,
    gaps: [
      gap(
        "design-ontology-release-ref-mismatch",
        "$.designOntologyReleaseRef",
        "The resolved Design Ontology release does not match the requested reference.",
        [releaseRef],
      ),
    ],
  }
}

function resolutionFailed(releaseRef: string): DesignBoundApplicationResult {
  return {
    ok: false,
    gaps: [
      gap(
        "design-ontology-release-resolution-failed",
        "$.designOntologyReleaseRef",
        "The Design Ontology release could not be resolved.",
        [releaseRef],
      ),
    ],
  }
}

function gap(
  code: DesignBoundApplicationGap["code"],
  path: string,
  message: string,
  evidenceRefs: string[],
): DesignBoundApplicationGap {
  return { code, path, message, evidenceRefs }
}

/**
 * Task 10 (Generic Application Ontology Program) — the evidence refs are the
 * ADMITTED contract refs of the authority context under diagnosis, not a
 * module-level customer literal. A mismatch reported before anything is
 * admitted carries only the hash it was given: an unadmitted ref must never
 * enter evidence, and a customer name must never enter this file.
 */
function projectAuthorityMismatch(
  contractHash: string,
  admittedContractRefs: readonly string[] = [],
): DesignBoundApplicationResult {
  return {
    ok: false,
    gaps: [
      {
        code: "project-validation-authority-invalid",
        path: "$.projectValidationAuthority.authorityContext",
        message:
          "The Library and Design composition halves must use the exact same project-validation authority context.",
        evidenceRefs: [...admittedContractRefs, contractHash],
      },
    ],
  }
}
